import { useQuery } from "react-query";
import { eventGuestAccessApi } from "../services/api";

// The paths come from our own API, but they are put straight into a QR code and
// the clipboard, so they are checked before use: only a same-origin relative
// path is accepted. "//evil.test/x" is a protocol-relative URL, not a path, and
// would send whoever scans the code off our origin.
const absoluteGuestUrl = (path) => {
  if (typeof path !== "string" || !path.startsWith("/") || path.startsWith("//")) {
    return "";
  }
  return `${window.location.origin}${path}`;
};

/**
 * KAN-10: the guest review and upload links for an event.
 *
 * The links used to be assembled in the browser from `collab_code`, which every
 * member who could *view* the event received in the event payload - so a
 * view-only member held a working guest-upload link for a project they were not
 * allowed to share. The links now come from an endpoint that answers 403 unless
 * the member can edit the event, and `forbidden` is what the share UI hides on.
 *
 * A 403 is an answer, not a failure: it is not retried and it raises no
 * snackbar, because "you cannot share this project" is a normal state for a
 * view-only member, not an error they did anything to cause.
 *
 * @param {number|string} eventId
 * @param {boolean} enabled skip the call entirely (no event selected yet)
 * @returns {{guestAccess: object|null, guestReviewUrl: string, guestUploadUrl: string,
 *            forbidden: boolean, isLoading: boolean, isError: boolean, refetch: function}}
 */
export default (eventId, enabled = true) => {
  const {
    data,
    error,
    isLoading,
    isError,
    refetch,
  } = useQuery(
    ["eventGuestAccess", eventId],
    () => eventGuestAccessApi({ id: eventId }),
    {
      enabled: !!enabled && !!eventId && !!window.localStorage.getItem("token"),
      retry: false,
    }
  );

  const status = error?.response?.status ?? error?.response?.data?.http_code;
  const guestAccess = data?.data?.data ?? null;

  return {
    guestAccess,
    guestReviewUrl: absoluteGuestUrl(guestAccess?.guest_review_path),
    guestUploadUrl: absoluteGuestUrl(guestAccess?.guest_upload_path),
    forbidden: status === 403,
    isLoading,
    isError,
    refetch,
  };
};
